import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function CitiesList() {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCity, setEditingCity] = useState(null);
    const [formData, setFormData] = useState({ name: '', province: '', region: '', notes: '' });

    useEffect(() => {
        fetchCities();
    }, []);

    const fetchCities = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/speeddate/api/cities');
            setCities(res.data);
        } catch (err) {
            console.error("Error fetching cities", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCity) {
                await axios.put(`/speeddate/api/cities/${editingCity.id}`, formData);
                window.Swal.fire({ title: 'Aggiornato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
            } else {
                await axios.post('/speeddate/api/cities', formData);
                window.Swal.fire({ title: 'Creato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
            }
            setFormData({ name: '', province: '', region: '', notes: '' });
            setEditingCity(null);
            fetchCities();
        } catch (err) {
            window.Swal.fire('Errore', err.response?.data?.error || 'Errore nel salvataggio', 'error');
        }
    };

    const handleEdit = (city) => {
        setEditingCity(city);
        setFormData({ name: city.name, province: city.province || '', region: city.region || '', notes: city.notes || '' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        const result = await window.Swal.fire({
            title: 'Sei sicuro?',
            text: "Questa operazione rimuoverà la città.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`/speeddate/api/cities/${id}`);
                fetchCities();
                window.Swal.fire('Eliminato!', 'La città è stata rimossa.', 'success');
            } catch (err) {
                window.Swal.fire('Errore', 'Impossibile eliminare la città (potrebbe essere usata in alcuni eventi).', 'error');
            }
        }
    };

    return (
        <div className="row g-4">
            <div className="col-lg-4">
                <div className="card border-0 shadow-sm glass">
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                        <h5 className="fw-bold mb-0">
                            <i className={`fas ${editingCity ? 'fa-edit text-success' : 'fa-plus-circle text-primary'} me-2`}></i>
                            {editingCity ? 'Modifica Città' : 'Nuova Città'}
                        </h5>
                    </div>
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label small text-uppercase fw-bold text-muted">Nome Città</label>
                                <input
                                    type="text"
                                    className="form-control rounded-3"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Es: Roma"
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label small text-uppercase fw-bold text-muted">Provincia</label>
                                <input
                                    type="text"
                                    className="form-control rounded-3"
                                    value={formData.province}
                                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                                    placeholder="Es: RM"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label small text-uppercase fw-bold text-muted">Regione</label>
                                <input
                                    type="text"
                                    className="form-control rounded-3"
                                    value={formData.region}
                                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                                    placeholder="Es: Lazio"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label small text-uppercase fw-bold text-muted">Note</label>
                                <textarea
                                    className="form-control rounded-3"
                                    rows="2"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Note aggiuntive..."
                                ></textarea>
                            </div>
                            <div className="d-grid gap-2">
                                <button type="submit" className={`btn ${editingCity ? 'btn-success' : 'btn-primary'} rounded-pill`}>
                                    <i className="fas fa-save me-2"></i> {editingCity ? 'Aggiorna' : 'Salva'}
                                </button>
                                {editingCity && (
                                    <button type="button" className="btn btn-link btn-sm text-secondary" onClick={() => { setEditingCity(null); setFormData({ name: '', province: '', region: '', notes: '' }); }}>
                                        Annulla
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="col-lg-8">
                <div className="card border-0 shadow-sm glass">
                    <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 fw-bold"><i className="fas fa-city text-primary me-2"></i>Anagrafica Città</h4>
                        <button className="btn btn-sm btn-outline-primary rounded-circle" onClick={fetchCities} disabled={loading}>
                            <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>
                    <div className="card-body px-4">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="small text-uppercase fw-bold text-muted border-0">Città</th>
                                        <th className="small text-uppercase fw-bold text-muted border-0">Località</th>
                                        <th className="small text-uppercase fw-bold text-muted border-0 text-end">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cities.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="text-center py-5">
                                                {loading ? (
                                                    <div className="spinner-border text-primary" role="status"></div>
                                                ) : (
                                                    <div className="text-muted">
                                                        <i className="fas fa-map-marker-alt fa-3x mb-3 opacity-25"></i>
                                                        <p className="mb-0">Nessuna città trovata</p>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : cities.map((c) => (
                                        <tr key={c.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-light text-primary rounded-circle p-2 me-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                                                        <i className="fas fa-city"></i>
                                                    </div>
                                                    <div>
                                                        <span className="fw-bold d-block text-dark">{c.name}</span>
                                                        {c.notes && <small className="text-muted d-block" style={{ fontSize: '11px' }}>{c.notes}</small>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <span className="fw-medium text-muted">{c.province}{c.province && c.region ? ', ' : ''}{c.region}</span>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <button className="btn btn-link btn-sm text-primary px-2" onClick={() => handleEdit(c)}>
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button className="btn btn-link btn-sm text-danger px-2" onClick={() => handleDelete(c.id)}>
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
